import { useState } from 'react';
import type { Identity } from '../types';
import { errorMessage } from '../lib/db';
import { hasSupabaseConfig } from '../lib/relay';
import {
  fetchOwnIdentityRow,
  isValidPhone,
  isValidUsername,
  normalizePhone,
  publishIdentityForUser,
  sendOtp,
  verifyOtp,
} from '../lib/identity';
import { loadAgeGate, saveAgeGate, saveIdentity } from '../lib/store';

type Props = {
  onRegistered: (me: Identity) => void;
  // When the app boots and finds a valid auth session but no local
  // secret key (new device, cleared data), it passes the session's
  // user id here. We skip phone+OTP and go straight to the username
  // step — the user re-publishes a new keypair under the same auth
  // identity. Friends will see the safety number change, which is
  // the right signal.
  resumeUserId?: string | null;
  resumeUsername?: string | null;
};

type Step = 'age' | 'blocked' | 'phone' | 'code' | 'username';

// Play Console target audience is declared 13+; the gate enforces it.
// Deliberately a one-bit question, not a birth-date prompt: EU/German
// data-minimization expectations make even an on-device date picker
// read as over-collection, and a date is no harder to fake than a
// button. Self-declaration is the accepted bar for a 13+ messenger
// with no stranger discovery (Signal/Telegram ask nothing at all).
// What gives it teeth is persistence: an "under 13" answer is
// remembered on the device and survives sign-out-and-wipe.

function initialStep(skipPhoneStep: boolean): Step {
  if (skipPhoneStep) return 'username';
  const gate = loadAgeGate();
  if (gate && 'failedAt' in gate) return 'blocked';
  if (gate && 'passedAt' in gate) return 'phone';
  return 'age';
}

export function Register({ onRegistered, resumeUserId, resumeUsername }: Props) {
  // `resuming` is true when we recognize this account from a prior register
  // (we have both an existing auth session AND a matching username on the
  // relay). `resumingAuthOnly` means we have a valid auth session but no
  // existing identity row — e.g. they OTP'd a brand-new phone number on
  // this device. In that case the "Welcome back" copy would be a lie, so
  // we treat the username step as a fresh registration even though we
  // skip the phone+OTP steps.
  const resuming = !!resumeUserId && !!resumeUsername;
  const resumingAuthOnly = !!resumeUserId && !resumeUsername;
  const skipPhoneStep = resuming || resumingAuthOnly;
  const [step, setStep] = useState<Step>(() => initialStep(skipPhoneStep));
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [userId, setUserId] = useState<string | null>(resumeUserId ?? null);
  const [username, setUsername] = useState(resumeUsername ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  function answerAgeGate(isThirteenPlus: boolean) {
    setError(null);
    if (!isThirteenPlus) {
      saveAgeGate({ failedAt: new Date().toISOString() });
      setStep('blocked');
      return;
    }
    saveAgeGate({ passedAt: new Date().toISOString() });
    setStep('phone');
  }

  async function submitPhone(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const cleaned = normalizePhone(phone);
    if (!isValidPhone(cleaned)) {
      setError('Phone must be in E.164 format, like +491701234567 or +13104573829.');
      return;
    }
    setBusy(true);
    try {
      await sendOtp(cleaned);
      setPhone(cleaned);
      setInfo(`We sent a code to ${cleaned}. It's a 6-digit number.`);
      setStep('code');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError('Enter the 6-digit code from the SMS.');
      return;
    }
    setBusy(true);
    try {
      const { userId: uid } = await verifyOtp(phone, trimmed);

      // If this auth user already has an identity row, this is a
      // returning user on a new device, not a new registration. Skip
      // the username step entirely — the phone number owns the
      // username, so we just publish a new device under the existing
      // name. Renames go through Settings → Change username.
      const existing = await fetchOwnIdentityRow(uid);
      if (existing) {
        setInfo(`Welcome back, @${existing.username}. Setting up this device…`);
        const me = await publishIdentityForUser({
          userId: uid,
          username: existing.username,
        });
        saveIdentity(me);
        onRegistered(me);
        return;
      }

      // Brand-new auth user — proceed to username step.
      setUserId(uid);
      setStep('username');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitUsername(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const clean = username.trim().toLowerCase();
    if (!isValidUsername(clean)) {
      setError('Username: 2–32 chars, lowercase letters, digits, _ . - only.');
      return;
    }
    if (!userId) {
      setError('Lost the verification context. Start over.');
      setStep('phone');
      return;
    }
    setBusy(true);
    try {
      const me = await publishIdentityForUser({ userId, username: clean });
      saveIdentity(me);
      onRegistered(me);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="screen screen-center">
      <div className="card card-narrow">
        <div className="brand">
          <span className="brand-dot" aria-hidden="true" />
          <span className="brand-name">No&nbsp;Rush</span>
        </div>
        <p className="eyebrow">Phase 1 prototype</p>

        {!hasSupabaseConfig && (
          <div className="alert">
            <strong>Supabase isn't configured.</strong> Copy <code>.env.example</code> to{' '}
            <code>.env.local</code>.
          </div>
        )}

        {step === 'age' && (
          <>
            <h1 className="h-display">One thing before we start.</h1>
            <p className="muted">
              No Rush is for people 13 and up. We won't ask your
              birthday — we don't want to know it. Just answer
              honestly. Your answer stays on this device.
            </p>
            <div className="form">
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => answerAgeGate(true)}
              >
                I'm 13 or older
              </button>
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => answerAgeGate(false)}
              >
                I'm under 13
              </button>
            </div>
            <p className="footnote">
              Why we ask: app-store rules require an age check before
              signup.
            </p>
          </>
        )}

        {step === 'blocked' && (
          <>
            <h1 className="h-display">Not just yet.</h1>
            <p className="muted">
              You can't create a No Rush account right now. We're glad
              you found us — come back when you're older.
            </p>
          </>
        )}

        {step === 'phone' && (
          <>
            <h1 className="h-display">Your phone is your anchor.</h1>
            <p className="muted">
              We send you a one-time code. We never use the number to
              notify you of anything else. Stored only as a hash.
            </p>
            <form onSubmit={submitPhone} className="form">
              <label className="field">
                <span className="field-label">Phone (with country code)</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+49 170 123 4567"
                  autoComplete="tel"
                  autoFocus
                  disabled={busy || !hasSupabaseConfig}
                />
              </label>
              {error && <p className="error">{error}</p>}
              <button
                className="btn btn-primary"
                type="submit"
                disabled={busy || !phone.trim() || !hasSupabaseConfig}
              >
                {busy ? 'Sending code…' : 'Send code'}
              </button>
            </form>
            <p className="footnote">
              The code is sent via Twilio. Standard SMS rates may apply on
              your end. There is no recurring SMS.
            </p>
          </>
        )}

        {step === 'code' && (
          <>
            <h1 className="h-display">Type the code we sent you.</h1>
            <p className="muted">{info ?? `Sent to ${phone}. Six digits.`}</p>
            <form onSubmit={submitCode} className="form">
              <label className="field">
                <span className="field-label">Six-digit code</span>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{6}"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="123456"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  disabled={busy}
                />
              </label>
              {error && <p className="error">{error}</p>}
              <button
                className="btn btn-primary"
                type="submit"
                disabled={busy || code.trim().length !== 6}
              >
                {busy ? 'Verifying…' : 'Verify'}
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setCode('');
                  setError(null);
                  setInfo(null);
                  setStep('phone');
                }}
                disabled={busy}
              >
                Use a different number
              </button>
            </form>
          </>
        )}

        {step === 'username' && (
          <>
            <h1 className="h-display">
              {resuming
                ? `Welcome back, @${resumeUsername}.`
                : 'Pick a name.'}
            </h1>
            <p className="muted">
              {resuming ? (
                <>
                  You're already verified, no new code needed. We'll
                  generate a fresh key on this device under your existing
                  account. Friends will see your safety number change,
                  which is the normal signal that you're back on a new
                  device.
                </>
              ) : resumingAuthOnly ? (
                <>
                  Your phone is verified. Pick a name and we'll publish
                  your keys. This is how friends find you. Your keys are
                  made on this device and never leave it.
                </>
              ) : (
                <>
                  This is how friends find you. Your keys are made on
                  this device and never leave it.
                </>
              )}
            </p>
            <form onSubmit={submitUsername} className="form">
              <label className="field">
                <span className="field-label">Username</span>
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="ermis"
                  autoComplete="off"
                  autoFocus
                  maxLength={32}
                  disabled={busy}
                />
              </label>
              {error && <p className="error">{error}</p>}
              <button
                className="btn btn-primary"
                type="submit"
                disabled={busy || !username.trim()}
              >
                {busy
                  ? resuming
                    ? 'Re-publishing…'
                    : 'Publishing your key…'
                  : resuming
                    ? 'Resume'
                    : 'Done'}
              </button>
            </form>
            <p className="footnote">
              Your secret key lives in your browser's local storage. If
              you clear browser data, you'll have to re-publish under a
              new key (and your friends will see the safety number change).
            </p>
          </>
        )}
      </div>
    </div>
  );
}
